/**
 * Demo Data Service
 *
 * Provides wrapper functions that enhance API responses with mock demo data.
 * Components call these functions after fetching real data - they don't need
 * to know about demo mode.
 *
 * To swap to real backend: modify these functions to return real data unchanged.
 */

import {
  isDemoTenant,
  isDemoMode,
  DEMO_CONFIG,
  injectDemoEnvironment,
  getMockHomeData,
  getMockHomeTrends,
  getMockAppOverview,
  getMockProblems,
  getMockClusters,
  mockRCAResults,
  mockReproductionConfig,
  mockFixConfig,
  generateClaudePrompt,
  getMockTraceDetail,
  getMockSpanDetail,
  getMockTraces,
  getMockClassifierResults,
  getMockSessions,
  getMockTraceDetections,
} from './mockDemoData';

/**
 * Enhance environment list response - inject demo env for Lumoz tenant.
 * Called after fetching /data/environments.
 *
 * @param {Object} response - API response with environments array
 * @param {string} tenantId - Current tenant ID
 * @returns {Object} - Enhanced response with demo environment if applicable
 */
export function enhanceEnvironmentsResponse(response, tenantId) {
  if (!isDemoTenant(tenantId)) return response;

  return {
    ...response,
    environments: injectDemoEnvironment(response.environments || [])
  };
}

/**
 * Enhance home data response - return mock data for demo env.
 * Called after fetching /data/home.
 *
 * @param {Object} response - API response with kpis and apps
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @returns {Object} - Mock data for demo, real data otherwise
 */
export function enhanceHomeResponse(response, tenantId, environment) {
  if (!isDemoMode(tenantId, environment)) return response;

  // The demo apps, and only those, for the same reason injectDemoEnvironment
  // drops real ones: a real app in this environment is a stray, not part of the
  // story being shown. Both paths have to agree, or an app hidden from the
  // picker still appears on the home list.
  return {
    ...response,
    apps: getMockHomeData().apps || []
  };
}

/**
 * Enhance home trends response - return mock trends for demo env.
 * Called after fetching /data/home/trends.
 *
 * @param {Object} response - API response with trend data
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} timeRange - Selected time range (24h, 7d, 30d, all-time)
 * @returns {Object} - Mock trends for demo, real data otherwise
 */
export function enhanceHomeTrendsResponse(response, tenantId, environment, timeRange = '7d', appId = null) {
  if (!isDemoMode(tenantId, environment)) return response;

  return getMockHomeTrends(timeRange, appId);
}

/**
 * Enhance app overview response - return mock data for demo app.
 * Called after fetching /data/home with app_id filter.
 *
 * @param {Object} response - API response with app data
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - App ID from route params
 * @returns {Object} - Mock data for demo app, real data otherwise
 */
export function enhanceAppOverviewResponse(response, tenantId, environment, appId) {
  if (!isDemoMode(tenantId, environment, appId)) return response;

  const mockData = getMockAppOverview(appId);
  if (!mockData) return response;

  return {
    ...mockData,
    apps: [mockData] // Mimic the structure expected by AppDetails.js
  };
}

/**
 * Enhance problems response - return mock problems for demo app.
 * Called after fetching /data/apps/{appId}/problems.
 *
 * @param {Object} response - API response with problems
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - App ID
 * @param {string} clusterId - Optional cluster ID to filter by
 * @returns {Object} - Mock problems for demo, real data otherwise
 */
export function enhanceProblemsResponse(response, tenantId, environment, appId, clusterId = null) {
  if (!isDemoMode(tenantId, environment, appId)) return response;

  return getMockProblems(clusterId, appId);
}

/**
 * Get cluster data for demo mode.
 * Returns null for non-demo contexts (clusters feature not available).
 *
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - App ID
 * @returns {Array|null} - Clusters array for demo, null otherwise
 */
export function getClusters(tenantId, environment, appId) {
  if (!isDemoMode(tenantId, environment, appId)) return null;

  return getMockClusters(appId);
}

/**
 * Get RCA result for a specific cluster.
 *
 * @param {string} clusterId - Cluster ID
 * @returns {Object|null} - RCA result or null if not found
 */
export function getRCAResult(clusterId) {
  return mockRCAResults[clusterId] || null;
}

/**
 * Get reproduction config for a specific cluster.
 *
 * @param {string} clusterId - Cluster ID
 * @returns {Object|null} - Reproduction config or null if not found
 */
export function getReproductionConfig(clusterId) {
  return mockReproductionConfig[clusterId] || null;
}

/**
 * Get fix config for a specific cluster.
 *
 * @param {string} clusterId - Cluster ID
 * @returns {Object|null} - Fix config or null if not found
 */
export function getFixConfig(clusterId) {
  return mockFixConfig[clusterId] || null;
}

/**
 * Generate Claude Code prompt for a cluster.
 *
 * @param {Object} cluster - Cluster object
 * @param {Object} rca - RCA result (optional, will use mock if not provided)
 * @param {Object} fix - Fix config (optional, will use mock if not provided)
 * @returns {string} - Generated prompt
 */
export function getClaudePrompt(cluster, rca = null, fix = null) {
  return generateClaudePrompt(cluster, rca, fix);
}

/**
 * Check if the current context is demo mode.
 * Useful for conditional rendering of demo-only features.
 *
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - Optional app ID
 * @returns {boolean} - True if in demo mode
 */
export function isInDemoMode(tenantId, environment, appId = null) {
  return isDemoMode(tenantId, environment, appId);
}

/**
 * Get demo config constants.
 * Useful for checking if an app ID matches the demo app.
 *
 * @returns {Object} - Demo config with tenant_id, environment, app_name, app_id
 */
export function getDemoConfig() {
  return DEMO_CONFIG;
}

/**
 * Enhance traces list response - return mock traces for demo app.
 * Called after fetching /data/apps/{appId}/traces.
 *
 * @param {Object} response - API response with traces
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - App ID
 * @param {string} problemId - Optional problem ID to filter by (the real API filters server-side)
 * @returns {Object} - Mock traces for demo app, real data otherwise
 */
export function enhanceTracesResponse(response, tenantId, environment, appId, problemId = null) {
  if (!isDemoMode(tenantId, environment, appId)) return response;

  return getMockTraces(appId, problemId);
}

/**
 * Enhance trace spans response - return mock spans for demo traces.
 * Called after fetching /data/apps/{appId}/traces/{traceId}/spans.
 *
 * @param {Object} response - API response with spans
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - App ID
 * @param {string} traceId - Trace ID
 * @returns {Object} - Mock spans for demo trace, real data otherwise
 */
export function enhanceTraceSpansResponse(response, tenantId, environment, appId, traceId) {
  if (!isDemoMode(tenantId, environment, appId)) return response;

  const mockData = getMockTraceDetail(traceId);
  if (!mockData) return response; // Not a demo trace

  return mockData;
}

/**
 * Mock trace-level signal rows for a demo trace (the /classifiers endpoint shape).
 *
 * @param {string} traceId - Trace ID
 * @returns {Array} - Signal rows, empty if the trace has none
 */
export function getDemoTraceDetections(traceId) {
  return getMockTraceDetections(traceId);
}

/**
 * Enhance span detail response - return mock span detail for demo traces.
 * Called after fetching /data/apps/{appId}/traces/{traceId}/spans/{spanId}.
 *
 * @param {Object} response - API response with span detail
 * @param {string} tenantId - Current tenant ID
 * @param {string} environment - Selected environment
 * @param {string} appId - App ID
 * @param {string} traceId - Trace ID
 * @param {string} spanId - Span ID
 * @returns {Object} - Mock span detail for demo, real data otherwise
 */
export function enhanceSpanDetailResponse(response, tenantId, environment, appId, traceId, spanId) {
  if (!isDemoMode(tenantId, environment, appId)) return response;

  const mockData = getMockSpanDetail(traceId, spanId);
  if (!mockData) return response; // Not a demo trace/span

  return mockData;
}

/**
 * Get mock classifier results for demo Signals tab detail view.
 * Called instead of /data/classifiers/results in demo mode.
 */
export function getDemoClassifierResults(tenantId, environment, appId, classifierKey) {
  if (!isDemoMode(tenantId, environment, appId)) return null;

  return getMockClassifierResults(classifierKey, appId);
}

/**
 * Enhance sessions response with mock data for demo apps.
 * Called after fetching /data/apps/{appId}/sessions (or on error for demo apps).
 */
export function enhanceSessionsResponse(response, tenantId, environment, appId) {
  if (!isDemoMode(tenantId, environment, appId)) return response;

  const mockData = getMockSessions(appId);
  return mockData || response;
}
